import { useMemo, type ReactElement } from 'react';
import type { RouteAnalyticsView, RouteSeatShareView } from '../../../domain/models/contracts';
import { ModalShell } from '../schedule/ModalShell';
import { CABIN_COLORS } from './chartTheme';
import {
  cabinRows,
  competitorRows,
  elasticityCurves,
  summarizeRoutes,
  revenuePassengerMilesOf,
  type CabinRow,
  type ElasticityCurve,
  type CompetitorRow,
  type NetworkSummary,
} from './routeAggregates';
import {
  formatCents,
  formatInt,
  formatPct,
  formatSignedPct,
  formatSignedPts,
  formatUsd,
  formatUsdCents,
} from './formatters';
import { PRICE_RESISTANCE_LABEL } from './routeMetrics';
import './RouteDetailModal.css';

export interface RouteDetailModalProps {
  readonly title: string;
  readonly subtitle: string;
  /**
   * Routes in scope. One route for the route view; the routes an aircraft flies
   * or that touch a hub for the fleet and hub views.
   */
  readonly routes: readonly RouteAnalyticsView[];
  readonly competitorShares: readonly RouteSeatShareView[];
  readonly onClose: () => void;
  /** Opens a single route in the Route inspector to edit its fares. */
  readonly onOpenInspector?: ((legId: string) => void) | undefined;
}

/**
 * Pop-out detail shared by the route, hub and fleet views. Every number comes
 * from the RouteAnalyticsView rows passed in; derived figures are labelled.
 */
export function RouteDetailModal({
  title,
  subtitle,
  routes,
  competitorShares,
  onClose,
  onOpenInspector,
}: RouteDetailModalProps): ReactElement {
  const summary = useMemo(() => summarizeRoutes(routes), [routes]);
  const cabins = useMemo(() => cabinRows(routes), [routes]);
  const curves = useMemo(() => elasticityCurves(routes), [routes]);
  const competitors = useMemo(() => competitorRows(routes, competitorShares), [routes, competitorShares]);
  const singleRoute = routes.length === 1 ? routes[0] : undefined;

  return (
    <ModalShell title={title} subtitle={subtitle} onClose={onClose} wide>
      <div className="rdm">
        {routes.length === 0 ? (
          <p className="rdm-empty">No published routes in this scope yet.</p>
        ) : (
          <>
            <LegPerformance routes={routes} summary={summary} />
            <YieldByClass rows={cabins} />
            <ElasticitySection curves={curves} />
            <CompetitorSection rows={competitors} cabins={cabins} />
            {singleRoute !== undefined && onOpenInspector !== undefined && (
              <footer className="rdm-footer">
                <button
                  type="button"
                  className="rdm-link-btn"
                  onClick={() => onOpenInspector?.(singleRoute.legId)}
                >
                  Edit fares in Route inspector
                </button>
              </footer>
            )}
          </>
        )}
      </div>
    </ModalShell>
  );
}

/* ------------------------------------------------------------------------ */

function LegPerformance({
  routes,
  summary,
}: {
  readonly routes: readonly RouteAnalyticsView[];
  readonly summary: NetworkSummary;
}): ReactElement {
  return (
    <section className="rdm-section" aria-label="Leg performance">
      <h3 className="rdm-heading">Leg performance</h3>
      <div className="rdm-table-wrap">
        <table className="rdm-table">
          <thead>
            <tr>
              <th scope="col">Route</th>
              <th scope="col">Aircraft</th>
              <th scope="col" className="num">Flights</th>
              <th scope="col" className="num">Pax / flight</th>
              <th scope="col" className="num">Load factor</th>
              <th scope="col" className="num">Revenue / flight</th>
              <th scope="col" className="num">Revenue</th>
              <th scope="col" className="num">Cost</th>
              <th scope="col" className="num">Profit</th>
              <th scope="col" className="num">Yield</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((route) => {
              const profit = route.revenueUSD - route.operatingCostUSD;
              const rpm = revenuePassengerMilesOf(route);
              return (
                <tr key={route.legId}>
                  <td>
                    {route.origin}–{route.destination}
                    <span className="rdm-muted"> · {route.legId}</span>
                  </td>
                  <td>{route.aircraftReg}</td>
                  <td className="num">
                    {formatInt(route.operatedFlights)}
                    <span className="rdm-muted"> / {formatInt(route.scheduledFlights)}</span>
                  </td>
                  <td className="num">{perFlight(route.passengers, route.operatedFlights, formatInt)}</td>
                  <td className="num">{formatPct(route.loadFactorPct)}</td>
                  <td className="num">{perFlight(route.revenueUSD, route.operatedFlights, formatUsd)}</td>
                  <td className="num">{formatUsd(route.revenueUSD)}</td>
                  <td className="num">{formatUsd(route.operatingCostUSD)}</td>
                  <td className={`num ${profit >= 0 ? 'pos' : 'neg'}`}>{formatUsd(profit)}</td>
                  <td className="num">{rpm > 0 ? formatCents((route.revenueUSD / rpm) * 100) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
          {routes.length > 1 && (
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td />
                <td className="num">
                  {formatInt(summary.operatedFlights)}
                  <span className="rdm-muted"> / {formatInt(summary.scheduledFlights)}</span>
                </td>
                <td className="num">{perFlight(summary.passengers, summary.operatedFlights, formatInt)}</td>
                <td className="num">{formatPct(summary.loadFactorPct)}</td>
                <td className="num">{perFlight(summary.revenueUSD, summary.operatedFlights, formatUsd)}</td>
                <td className="num">{formatUsd(summary.revenueUSD)}</td>
                <td className="num">{formatUsd(summary.operatingCostUSD)}</td>
                <td className={`num ${summary.profitUSD >= 0 ? 'pos' : 'neg'}`}>{formatUsd(summary.profitUSD)}</td>
                <td className="num">{formatCents(summary.yieldCentsPerRpm)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="rdm-note">
        Figures are from the most recent daily close. Yield is revenue per revenue passenger-mile (RPK), where RPK is
        derived as available seat-miles x load factor.
      </p>
    </section>
  );
}

function perFlight(total: number, flights: number, format: (value: number) => string): string {
  return flights > 0 ? format(total / flights) : '—';
}

/* ------------------------------------------------------------------------ */

function YieldByClass({ rows }: { readonly rows: readonly CabinRow[] }): ReactElement {
  return (
    <section className="rdm-section" aria-label="Yield by seat class">
      <h3 className="rdm-heading">Yield by seat class</h3>
      <div className="rdm-table-wrap">
        <table className="rdm-table">
          <thead>
            <tr>
              <th scope="col">Seat class</th>
              <th scope="col" className="num">Seats installed</th>
              <th scope="col" className="num">Fare on offer</th>
              <th scope="col" className="num">Fare per mile</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.cabin} className={row.fareUSD === null ? 'rdm-off' : undefined}>
                <td>
                  <span className="rdm-swatch" style={{ background: CABIN_COLORS[row.cabin] }} aria-hidden="true" />
                  {CABIN_LABELS[row.cabin]}
                </td>
                <td className="num">{formatInt(row.seatsInstalled)}</td>
                <td className="num">{row.fareUSD === null ? 'Not sold' : formatUsdCents(row.fareUSD)}</td>
                <td className="num">
                  {row.fareCentsPerMile === null ? '—' : formatCents(row.fareCentsPerMile)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="rdm-note">
        Fares are seat-weighted across the routes in scope. The route view does not split passengers or revenue by
        cabin, so this shows the fare on offer rather than realised yield by class.
      </p>
    </section>
  );
}

const CABIN_LABELS: Readonly<Record<CabinRow['cabin'], string>> = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

/* ------------------------------------------------------------------------ */

function ElasticitySection({ curves }: { readonly curves: readonly ElasticityCurve[] }): ReactElement {
  return (
    <section className="rdm-section" aria-label="Price elasticity">
      <h3 className="rdm-heading">Price elasticity</h3>
      {curves.length === 0 ? (
        <p className="rdm-empty">No cabin is sold on these routes, so there is no demand curve.</p>
      ) : (
        <>
          <ElasticityChart curves={curves} />
          <div className="rdm-table-wrap">
            <table className="rdm-table">
              <thead>
                <tr>
                  <th scope="col">Seat class</th>
                  <th scope="col" className="num">Elasticity</th>
                  <th scope="col" className="num">Demand at +10% fare</th>
                  <th scope="col" className="num">Demand at -10% fare</th>
                  <th scope="col">Price resistance</th>
                </tr>
              </thead>
              <tbody>
                {curves.map((curve) => {
                  const up = curve.points.find((p) => p.fareIndexPct === 110);
                  const down = curve.points.find((p) => p.fareIndexPct === 90);
                  return (
                    <tr key={curve.cabin}>
                      <td>
                        <span className="rdm-swatch" style={{ background: CABIN_COLORS[curve.cabin] }} aria-hidden="true" />
                        {CABIN_LABELS[curve.cabin]}
                      </td>
                      <td className="num">{curve.elasticity.toFixed(2)}</td>
                      <td className="num">{up === undefined ? '—' : formatSignedPct(up.demandChangePct)}</td>
                      <td className="num">{down === undefined ? '—' : formatSignedPct(down.demandChangePct)}</td>
                      <td>{PRICE_RESISTANCE_LABEL[resistanceOf(curve.elasticity)]}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="rdm-note">
            Constant-elasticity curve (demand scales with fare^elasticity) drawn around each route&apos;s current fare,
            using the worker&apos;s per-cabin elasticity. Multi-route scopes use a seat-weighted elasticity. This is a
            planning approximation; the worker routes passengers with a logit choice model, so the realised response can
            differ.
          </p>
        </>
      )}
    </section>
  );
}

/** Same thresholds as the Route inspector's price sensitivity rating at a 10% fare rise. */
function resistanceOf(elasticity: number): 'LOW' | 'BALANCED' | 'HIGH' {
  const change = (Math.pow(1.1, elasticity) - 1) * 100;
  if (change > -5) return 'LOW';
  if (change > -20) return 'BALANCED';
  return 'HIGH';
}

const CHART = { width: 560, height: 220, left: 46, right: 12, top: 12, bottom: 28 } as const;
const FARE_MIN = 70;
const FARE_MAX = 130;

function ElasticityChart({ curves }: { readonly curves: readonly ElasticityCurve[] }): ReactElement {
  const values = curves.flatMap((curve) => curve.points.map((point) => point.demandChangePct));
  const yMin = Math.min(0, ...values);
  const yMax = Math.max(0, ...values);
  const ySpan = yMax - yMin > 0 ? yMax - yMin : 1;
  const innerWidth = CHART.width - CHART.left - CHART.right;
  const innerHeight = CHART.height - CHART.top - CHART.bottom;

  const x = (fareIndexPct: number): number =>
    CHART.left + ((fareIndexPct - FARE_MIN) / (FARE_MAX - FARE_MIN)) * innerWidth;
  const y = (demandChangePct: number): number => CHART.top + ((yMax - demandChangePct) / ySpan) * innerHeight;

  return (
    <figure className="rdm-chart">
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        role="img"
        aria-label="Demand change against fare change for each seat class"
      >
        <line className="rdm-axis" x1={CHART.left} x2={CHART.width - CHART.right} y1={y(0)} y2={y(0)} />
        <line className="rdm-axis" x1={x(100)} x2={x(100)} y1={CHART.top} y2={CHART.height - CHART.bottom} />
        <text className="rdm-tick" x={CHART.left - 6} y={y(yMax)} textAnchor="end" dominantBaseline="hanging">
          {formatSignedPct(yMax, 0)}
        </text>
        <text className="rdm-tick" x={CHART.left - 6} y={y(yMin)} textAnchor="end" dominantBaseline="auto">
          {formatSignedPct(yMin, 0)}
        </text>
        {[FARE_MIN, 100, FARE_MAX].map((tick) => (
          <text key={tick} className="rdm-tick" x={x(tick)} y={CHART.height - 8} textAnchor="middle">
            {tick === 100 ? 'Today' : `${tick}%`}
          </text>
        ))}
        {curves.map((curve) => {
          const path = curve.points
            .map((point, index) => `${index === 0 ? 'M' : 'L'}${x(point.fareIndexPct).toFixed(1)},${y(point.demandChangePct).toFixed(1)}`)
            .join(' ');
          return <path key={curve.cabin} d={path} fill="none" stroke={CABIN_COLORS[curve.cabin]} strokeWidth={2} />;
        })}
        <circle cx={x(100)} cy={y(0)} r={3.5} className="rdm-today" />
      </svg>
      <figcaption className="rdm-legend">
        <span className="rdm-legend-axis">x: fare vs today · y: demand vs today</span>
        {curves.map((curve) => (
          <span key={curve.cabin} className="rdm-legend-item">
            <span className="rdm-swatch" style={{ background: CABIN_COLORS[curve.cabin] }} aria-hidden="true" />
            {CABIN_LABELS[curve.cabin]}
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------------ */

function CompetitorSection({
  rows,
  cabins,
}: {
  readonly rows: readonly CompetitorRow[];
  readonly cabins: readonly CabinRow[];
}): ReactElement {
  const sold = cabins.filter((row) => row.fareUSD !== null);
  return (
    <section className="rdm-section" aria-label="Competitor comparison">
      <h3 className="rdm-heading">Competitor comparison</h3>
      <div className="rdm-table-wrap">
        <table className="rdm-table">
          <thead>
            <tr>
              <th scope="col">Route</th>
              <th scope="col" className="num">Competing carriers</th>
              <th scope="col" className="num">Their load factor</th>
              <th scope="col" className="num">Load factor gap</th>
              <th scope="col" className="num">Our seat share</th>
              <th scope="col" className="num">Largest rival share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.route.legId}>
                <td>{row.route.origin}–{row.route.destination}</td>
                <td className="num">{formatInt(row.competitorCount)}</td>
                <td className="num">
                  {row.competitorLoadFactorPct === null ? 'Not measured' : formatPct(row.competitorLoadFactorPct)}
                </td>
                <td className="num">
                  {row.loadFactorGapPts === null ? '—' : formatSignedPts(row.loadFactorGapPts)}
                </td>
                <td className="num">{row.playerSeatSharePct === null ? '—' : formatPct(row.playerSeatSharePct)}</td>
                <td className="num">{row.largestRivalSharePct === null ? '—' : formatPct(row.largestRivalSharePct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sold.length > 0 && (
        <div className="rdm-table-wrap">
          <table className="rdm-table">
            <thead>
              <tr>
                <th scope="col">Seat class</th>
                <th scope="col" className="num">Our fare</th>
                <th scope="col" className="num">Market reference fare</th>
                <th scope="col" className="num">Fare vs market</th>
              </tr>
            </thead>
            <tbody>
              {sold.map((row) => (
                <tr key={row.cabin}>
                  <td>{CABIN_LABELS[row.cabin]}</td>
                  <td className="num">{row.fareUSD === null ? '—' : formatUsdCents(row.fareUSD)}</td>
                  <td className="num">{row.marketFareUSD === null ? '—' : formatUsdCents(row.marketFareUSD)}</td>
                  <td className="num">{row.fareVsMarketPct === null ? '—' : formatSignedPct(row.fareVsMarketPct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="rdm-note">
        The route view does not carry individual competitor fares, so fares are compared with the market reference fare
        per cabin. Seat shares come from the published schedules of all carriers on each O&amp;D market. A dash means
        the worker has not reported that figure for the route.
      </p>
    </section>
  );
}
